import { QRCodeCanvas } from "qrcode.react";
import { useRef, useState, useEffect } from "react";
import { Box, Typography, Button, CircularProgress } from "@mui/material";
import {
  Download as DownloadIcon,
  Print as PrintIcon,
  Refresh as RefreshIcon,
  ContentCopy as CopyIcon,
} from "@mui/icons-material";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { GetAdminQR, RegenerateAdminQR } from "../../../Services/Services";

function QRCodeCard() {
  const qrRef = useRef();

  const [qrToken, setQrToken] = useState("");
  const [qrUrl, setQrUrl] = useState("");
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);

  const FRONTEND_URL = window.location.origin;

  useEffect(() => {
    loadAdminQR();
  }, []);

  const loadAdminQR = async () => {
    setLoading(true);
    try {
      const data = await GetAdminQR();
      if (data?.qr_token) {
        const token = data.qr_token;
        setQrToken(token);
        setQrUrl(`${FRONTEND_URL}/mark-attendance?token=${token}`);
      } else {
        await regenerateQR(true);
      }
    } catch (err) {
      console.log("Error loading admin QR:", err);
      toast.error(err.response?.data?.message || "Failed to load QR");
    } finally {
      setLoading(false);
    }
  };

  const regenerateQR = async (skipConfirm = false) => {
    if (!skipConfirm && !window.confirm("Regenerate Office QR? Old QR will stop working.")) return;

    setRegenerating(true);
    try {
      const data = await RegenerateAdminQR();
      if (data?.qr_token) {
        const token = data.qr_token;
        setQrToken(token);
        setQrUrl(`${FRONTEND_URL}/mark-attendance?token=${token}`);
        if (!skipConfirm) {
          toast.success("Office QR regenerated successfully!");
        }
      }
    } catch (err) {
      console.log(err);
      toast.error(err.response?.data?.message || "Failed to regenerate QR");
    } finally {
      setRegenerating(false);
    }
  };

  const downloadQR = () => {
    const canvas = qrRef.current?.querySelector("canvas");
    if (!canvas) {
      toast.error("QR code not found");
      return;
    }

    const newCanvas = document.createElement("canvas");
    const padding = 40;
    const textHeight = 80;
    newCanvas.width = canvas.width + padding * 2;
    newCanvas.height = canvas.height + padding * 2 + textHeight;

    const ctx = newCanvas.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, newCanvas.width, newCanvas.height);

    ctx.fillStyle = "#1a1a1a";
    ctx.font = "bold 18px Arial";
    ctx.textAlign = "center";
    ctx.fillText("🏢 Office Attendance QR", newCanvas.width / 2, 28);

    ctx.drawImage(canvas, padding, padding + 10);

    ctx.fillStyle = "#555555";
    ctx.font = "12px Arial";
    ctx.fillText(
      "Scan with Employee App or Camera",
      newCanvas.width / 2,
      canvas.height + padding + 40
    );
    ctx.fillText(
      "to mark your attendance",
      newCanvas.width / 2,
      canvas.height + padding + 60
    );

    const link = document.createElement("a");
    link.href = newCanvas.toDataURL("image/png");
    link.download = "Office_Attendance_QR.png";
    link.click();
    toast.success("QR downloaded!");
  };

  const printQR = () => window.print();

  const copyUrl = () => {
    if (!qrUrl) return;
    navigator.clipboard.writeText(qrUrl);
    toast.success("URL copied to clipboard!");
  };

  return (
    <>
      <ToastContainer />
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 1.5,
          p: 2,
          width: "100%",
        }}
      >
        <Typography sx={{ fontSize: "18px", fontWeight: 700, color: "#1e293b" }}>
          🏢 Office Attendance QR
        </Typography>

        <Typography sx={{ fontSize: "12px", color: "#64748b", textAlign: "center", px: 2 }}>
          Employees scan this with Employee App or Camera to mark attendance
        </Typography>

        {loading ? (
          <Box sx={{ py: 4 }}>
            <CircularProgress sx={{ color: "#ff8800" }} />
          </Box>
        ) : (
          <>
            <Box
              ref={qrRef}
              sx={{
                p: 2,
                bgcolor: "#fff",
                borderRadius: "12px",
                border: "2px solid #f1f5f9",
                boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
              }}
            >
              {qrToken ? (
                <QRCodeCanvas
                  value={qrToken}
                  size={200}
                  level="H"
                  includeMargin={true}
                />
              ) : (
                <Typography sx={{ color: "#94a3b8", py: 5, px: 3 }}>
                  Click Regenerate to create QR
                </Typography>
              )}
            </Box>

            {qrUrl && (
              <Box
                sx={{
                  width: "100%",
                  maxWidth: 260,
                  p: 1,
                  bgcolor: "#f8fafc",
                  borderRadius: "6px",
                  border: "1px dashed #cbd5e1",
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                }}
              >
                <Typography
                  sx={{
                    fontSize: "10px",
                    color: "#64748b",
                    fontFamily: "monospace",
                    wordBreak: "break-all",
                    flex: 1,
                    lineHeight: 1.3,
                  }}
                >
                  {qrUrl.length > 55 ? `${qrUrl.substring(0, 55)}...` : qrUrl}
                </Typography>
                <Button
                  onClick={copyUrl}
                  sx={{
                    minWidth: "auto",
                    p: 0.5,
                    color: "#64748b",
                    "&:hover": { color: "#ff8800", bgcolor: "transparent" },
                  }}
                >
                  <CopyIcon sx={{ fontSize: 14 }} />
                </Button>
              </Box>
            )}

            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", justifyContent: "center", mt: 1 }}>
              <Button
                onClick={downloadQR}
                startIcon={<DownloadIcon />}
                disabled={!qrToken}
                sx={{
                  bgcolor: "#059669", color: "#fff",
                  px: 2, py: 0.75, borderRadius: "6px",
                  fontSize: "13px", fontWeight: 600,
                  textTransform: "none",
                  "&:hover": { bgcolor: "#047857" },
                }}
              >
                Download
              </Button>

              <Button
                onClick={() => regenerateQR(false)}
                startIcon={regenerating ? <CircularProgress size={16} /> : <RefreshIcon />}
                disabled={regenerating}
                sx={{
                  bgcolor: "#3b82f6", color: "#fff",
                  px: 2, py: 0.75, borderRadius: "6px",
                  fontSize: "13px", fontWeight: 600,
                  textTransform: "none",
                  "&:hover": { bgcolor: "#2563eb" },
                }}
              >
                {regenerating ? "Regenerating..." : "Regenerate"}
              </Button>

              <Button
                onClick={printQR}
                startIcon={<PrintIcon />}
                disabled={!qrToken}
                sx={{
                  bgcolor: "#FCF5EB", color: "#000",
                  border: "2px solid transparent",
                  px: 2, py: 0.75, borderRadius: "6px",
                  fontSize: "13px", fontWeight: 600,
                  textTransform: "none",
                  "&:hover": {
                    border: "2px solid #ff8800",
                    bgcolor: "#FCF5EB",
                  },
                }}
              >
                Print
              </Button>
            </Box>
          </>
        )}
      </Box>
    </>
  );
}

export default QRCodeCard;